import { Button } from '@creditornot/cb-button';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ReportLink, downloadReportLink } from 'apis/merchant-gateway/insights';
import { Typography } from 'common/typography';
import { downloadFromUrl } from 'common/utils/download';
import Loading from 'core/Loading';

import {
  ScreenCenteredImage,
  ScreenContainer,
  ScreenInformation,
  ScreenHeader,
  ScreenActionsContainer,
} from './screens.styled';
import HorribleImage from '../../../images/horrible.png';
import ReceiptImage from '../../../images/receipt.png';

export const Download = ({ onBack }: { onBack: () => void }) => {
  const { reportId } = useParams<{ reportId: string }>();
  const { locale } = useIntl();
  const { data, isLoading, isError } = useQuery(['report', reportId], () => downloadReportLink({ reportId, locale }), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  const triggerDownload = useCallback((data: ReportLink) => {
    downloadFromUrl(data.url);
  }, []);

  useEffect(() => {
    if (data && data.url && data.expiryDate) {
      triggerDownload(data);
    }
  }, [data, triggerDownload]);

  return (
    <ScreenContainer>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {isError ? (
            <>
              <ScreenCenteredImage src={HorribleImage} />
              <ScreenInformation>
                <ScreenHeader>
                  <FormattedMessage id="insights.reports.result.error.title" />
                </ScreenHeader>
                <Typography.Body3>
                  <FormattedMessage id="insights.reports.result.error.description" />
                </Typography.Body3>
              </ScreenInformation>
            </>
          ) : (
            <>
              <ScreenCenteredImage src={ReceiptImage} />
              <ScreenInformation>
                <ScreenHeader>
                  <FormattedMessage id="insights.reports.download.title" />
                </ScreenHeader>
                <Typography.Body3>
                  <FormattedMessage
                    id="insights.reports.download.description"
                    values={{
                      link: chunks => (
                        <a href={data.url} target="_blank" rel="noreferrer" onClick={() => triggerDownload(data)}>
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </Typography.Body3>
                <Typography.Body3>
                  <FormattedMessage id="insights.reports.download.enjoy" />
                </Typography.Body3>
              </ScreenInformation>
            </>
          )}
          <ScreenActionsContainer>
            <Button variant="primary" size="small" onClick={onBack}>
              <FormattedMessage id="insights.reports.result.back-to-insights" />
            </Button>
          </ScreenActionsContainer>
        </>
      )}
    </ScreenContainer>
  );
};
